import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  DesktopClient,
  DeviceType,
  isHardwareDevice,
} from "../../../api/firebase/types/device.types";
import ScenarioSelection from "./formFields/ScenarioSelection";
import { useFormik } from "formik";
import UpdateAdressInput from "../deviceLocation/UpdateAdressInput";
import DeviceContactOptionTable from "./formFields/DeviceContactOptionTable";
import ContactSelectionModal from "../../../components/dialogs/ContactSelectionModal";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { mailRegEx } from "../../../api/validation";

export type DeviceSettingsFormData = Pick<
  DeviceType,
  "name" | "contactSettings" | "location" | "exactPosition"
> &
  Pick<DesktopClient, "email"> & {
    scenarioIds: Set<string>;
  };

type DeviceSettingsFormProps = {
  currentDevice: DeviceType | DesktopClient;
  onSave: (device: DeviceType | DesktopClient) => void;
};

const DeviceSettingsForm: React.FC<DeviceSettingsFormProps> = ({
  currentDevice,
  onSave,
}) => {
  useEffect(() => {
    formik.setValues(
      {
        name: currentDevice.name || "",
        location: (currentDevice as DeviceType).location || undefined,
        contactSettings: currentDevice.contactSettings || new Map(),
        scenarioIds: new Set(currentDevice.scenarioIds) || new Set(),
        exactPosition: currentDevice.exactPosition || "",
        email: (currentDevice as DesktopClient).email || "",
      },
      false
    );
    return () => {
      formik.resetForm();
    };
  }, [currentDevice]);

  const formik = useFormik<DeviceSettingsFormData>({
    initialValues: {
      name: currentDevice.name || "",
      location: (currentDevice as DeviceType).location || undefined,
      contactSettings: currentDevice.contactSettings || new Map(),
      scenarioIds: new Set(currentDevice.scenarioIds) || new Set(),
      exactPosition: currentDevice.exactPosition || "",
      email: (currentDevice as DesktopClient).email || "",
    },
    validate: (data) => {
      let errors: Partial<Record<keyof DeviceSettingsFormData, string>> = {};

      if (!data.name) {
        errors.name = "Bitte geben Sie der Meldeinstanz eine Bezeichnung";
      }

      if (isHardwareDevice(currentDevice)) {
        if (!data.location)
          errors.location = "Bitte geben Sie der Meldeinstanz eine Adresse";
      } else {
        if (!data.email) {
          errors.email = "Bitte weisen Sie der Meldeinstanz eine Email zu";
        } else if (!mailRegEx.test(data.email)) {
          errors.email = "Ungültige Email Adresse";
        }
      }
      return errors;
    },
    onSubmit: async (data) => {
      const updatedDevice = {
        ...currentDevice,
        ...data,
        scenarioIds: Array.from(data.scenarioIds),
      };
      await onSave(updatedDevice);
    },
  });

  const isFormFieldValid = (name: keyof DeviceSettingsFormData) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getFormErrorMessage = (name: keyof DeviceSettingsFormData) =>
    isFormFieldValid(name) ? (
      <small className="p-error p-d-block form-error">
        {formik.errors[name]}
      </small>
    ) : undefined;

  return (
    <form id="device-settings" onSubmit={formik.handleSubmit}>
      <h3>Name</h3>
      <InputText
        id="name"
        className="p-m-2"
        value={formik.values.name}
        onChange={formik.handleChange}
        placeholder="z.B. Management"
      />
      {getFormErrorMessage("name")}
      {isHardwareDevice(currentDevice) && (
        <>
          <h3>Seriennummer</h3>
          <InputText
            id="email"
            className="p-m-2"
            value={currentDevice.serialNumber}
            disabled
          />
          <h3>SOS-Adresse</h3>
          <UpdateAdressInput
            location={formik.values.location}
            handleLocationChange={(location) =>
              formik.setFieldValue("location", location)
            }
          />
          {getFormErrorMessage("location")}
        </>
      )}
      {!isHardwareDevice(currentDevice) && (
        <>
          <h3>Email</h3>
          <InputText
            id="email"
            className="p-m-2"
            value={formik.values.email}
            onChange={formik.handleChange}
            disabled
          />
          {getFormErrorMessage("email")}
        </>
      )}
      <h3>Genauer Standort</h3>
      <InputTextarea
        id="exactPosition"
        value={formik.values.exactPosition}
        onChange={formik.handleChange}
        className="p-m-2"
        placeholder="z.B. 3.OG, Raum 306"
        rows={5}
        cols={50}
        autoResize
      />
      <h3>Verfügbare Szenarien</h3>
      <ScenarioSelection
        selectedScenarios={formik.values.scenarioIds}
        setSelectedScenarios={(selectedScenarios) =>
          formik.setFieldValue("scenarioIds", selectedScenarios)
        }
        deviceType={currentDevice.typeCategory}
      />
      <DeviceContactOptionTable
        contactSettings={formik.values.contactSettings}
        onChange={(newContactSettings) =>
          formik.setFieldValue("contactSettings", newContactSettings)
        }
      />
      <ContactSelectionModal />
    </form>
  );
};

export default observer(DeviceSettingsForm);
